import ExpandLessIcon from '@material-design-icons/svg/filled/expand_less.svg?react';
import ExpandMoreIcon from '@material-design-icons/svg/filled/expand_more.svg?react';
import { getHubspotSubmissionKind } from '../../../lib/hubspot-management/submissionKind';
import type { UserRole } from '../../../utils/roleUtils';
import {
  getHubspotTicketReviewChecklistContext,
  hubspotTicketReviewChecklistItems,
} from './reviewChecklist';

interface HubspotTicketReviewChecklistProps {
  ticketId: string;
  viewerRole: UserRole;
  requestType: string | null;
  content: string | null;
  hasPublishedReleaseHistory: boolean | null;
  completedChecklistItemIds: string[];
  expandedChecklistItemIds: string[];
  onToggleChecklistItem: (itemId: string) => void;
  onToggleChecklistGuidance: (itemId: string) => void;
}

export function HubspotTicketReviewChecklist({
  ticketId,
  viewerRole,
  requestType,
  content,
  hasPublishedReleaseHistory,
  completedChecklistItemIds,
  expandedChecklistItemIds,
  onToggleChecklistItem,
  onToggleChecklistGuidance,
}: HubspotTicketReviewChecklistProps) {
  const submissionKind = getHubspotSubmissionKind({
    requestType,
    content,
    hasPublishedReleaseHistory,
  });
  const checklistContext =
    getHubspotTicketReviewChecklistContext(submissionKind);
  const isModerator = viewerRole === 'moderator';
  const totalChecklistItems = hubspotTicketReviewChecklistItems.length;
  const completedChecklistCount = completedChecklistItemIds.length;
  const isChecklistComplete = completedChecklistCount === totalChecklistItems;

  return (
    <div className="rounded-lg border border-neutral-700/50 bg-neutral-800/30 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-white">Review Checklist</h3>
          <div className="mt-1 text-xs text-neutral-400">
            {checklistContext}
          </div>
        </div>
        <div className="shrink-0 rounded-full border border-neutral-700 bg-neutral-900 px-2.5 py-1 text-[11px] font-medium text-neutral-300">
          {completedChecklistCount}/{totalChecklistItems} complete
        </div>
      </div>

      <div className="space-y-2">
        {hubspotTicketReviewChecklistItems.map((item) => {
          const isCompleted = completedChecklistItemIds.includes(item.id);
          const isExpanded = expandedChecklistItemIds.includes(item.id);
          const contentId = `ticket-review-checklist-${ticketId}-${item.id}`;

          return (
            <div
              key={item.id}
              className="rounded-md border border-neutral-800 bg-neutral-900/70"
            >
              <div className="flex items-center gap-3 px-3 py-2.5">
                <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    aria-label={item.title}
                    checked={isCompleted}
                    onChange={() => onToggleChecklistItem(item.id)}
                    className="size-4 shrink-0 accent-teal-500"
                  />
                  <span className="text-sm text-neutral-100">{item.title}</span>
                </label>
                <button
                  type="button"
                  onClick={() => onToggleChecklistGuidance(item.id)}
                  aria-expanded={isExpanded}
                  aria-controls={contentId}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-neutral-200"
                >
                  <span>{isExpanded ? 'Hide details' : 'Details'}</span>
                  {isExpanded ? (
                    <ExpandLessIcon className="size-4 fill-current" />
                  ) : (
                    <ExpandMoreIcon className="size-4 fill-current" />
                  )}
                </button>
              </div>

              {isExpanded && (
                <div
                  id={contentId}
                  className="space-y-1.5 border-t border-neutral-800 px-3 py-2 text-xs text-neutral-400"
                >
                  {item.guidance.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div
        className={`text-xs ${
          isModerator
            ? isChecklistComplete
              ? 'text-emerald-400'
              : 'text-amber-300'
            : 'text-neutral-500'
        }`}
      >
        {isModerator
          ? isChecklistComplete
            ? 'Checklist complete. Moderators can save a status change.'
            : 'Moderators must complete every checklist item before saving a status change.'
          : 'Admins and superadmins can save without completing every item, but the checklist stays visible for consistency.'}
      </div>
    </div>
  );
}
